import { client } from '.';
import {
  AccountStatusWithLocations,
  BranchUsersResponse,
  GetNestedLocationByIdResponse,
  NestedLocation,
  NestedLocationParams,
  NestedLocationPayload,
  NestedLocationResponse,
  PaginatedNestedLocationsParams,
  PaginatedNestedLocationsResponse,
} from '../types/nestedLocations';
import { SortByNameOrder } from '../utils/sortByName';

/**
 * Request parameters for the tree's active sort order.
 *
 * `unsorted` contributes nothing, which is what keeps the request byte-identical
 * to today's for every caller that never sorts. `asc` sends no `desc` either: the
 * service defaults to ascending, and an explicit `desc=false` is one more thing a
 * future coercion change could read the wrong way.
 */
export const sortOrderParams = (
  order: SortByNameOrder,
): Pick<NestedLocationParams, 'order' | 'desc'> => {
  if (order === 'unsorted') return {};
  return order === 'asc' ? { order: 'name' } : { order: 'name', desc: true };
};

const getNestedLocations = async (params: NestedLocationParams = {}, signal?: AbortSignal) => {
  const response = await client.get<NestedLocationResponse>('/nestable_locations', {
    params,
    signal,
  });
  return response.data;
};

const getAllLocations = async () => {
  const response = await client.get<NestedLocation[]>('/nestable_locations/all_locations');
  return response.data;
};

const getPaginatedLocations = async (params: PaginatedNestedLocationsParams = {}) => {
  const response = await client.get<PaginatedNestedLocationsResponse>(
    '/nestable_locations/paginated_locations',
    { params },
  );
  return response.data;
};

const checkAccountStatusWithLocations = async () => {
  const response = await client.get<AccountStatusWithLocations>(
    '/nestable_locations/check_account_locations',
  );
  return response.data;
};

const deleteNestedLocations = async (locationId: number) => {
  const response = await client.delete<string>(`/nestable_locations/${locationId}`);
  return response.data;
};

const createNestedLocation = async (data: NestedLocationPayload) => {
  const response = await client.post<NestedLocationResponse>('/nestable_locations', data);
  return response.data;
};

const updateNestedLocation = async (locationId: number, data: Partial<NestedLocationPayload>) => {
  const response = await client.put<NestedLocationResponse>(
    `/nestable_locations/${locationId}`,
    data,
  );
  return response.data;
};

export const exportNestedLocations = async (locationsIds: number[] = []) => {
  return client.get('/nestable_locations/export', {
    params: locationsIds.length ? { ids: locationsIds.join(',') } : undefined,
  });
};

const importNestedLocations = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await client.post<{ message: string; error?: string }>(
    '/nestable_locations/import',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    },
  );
  return response.data;
};

const exportNestedLocationsTemplate = async () => {
  const response = await client.get<{ download_url: string; filename?: string }>(
    '/nestable_locations/export_template',
  );
  return response.data;
};

const exportLegacyLocations = async () => {
  const response = await client.get<Blob>('/locations.xlsx', { responseType: 'blob' });
  return new Blob([response.data]);
};

const getNestedLocationById = async (id: number) => {
  const response = await client.get<GetNestedLocationByIdResponse>(`/nestable_locations/${id}`);
  return response.data;
};

// DS-4062: users with an active grant on this location or any of its
// descendants (any depth), deduplicated. Deliberately unpaginated -- the
// Usuarios block (DS-4043, AC-9b) renders every one of them with its own
// internal scroll, not a truncated quick view like the other 6 blocks.
const getBranchUsers = async (locationId: number, search?: string) => {
  const response = await client.get<BranchUsersResponse>(`/nestable_locations/${locationId}/users`, {
    params: search ? { search } : undefined,
  });
  return response.data;
};

export const nestedLocationsApi = {
  getNestedLocations,
  getNestedLocationById,
  getBranchUsers,
  getAllLocations,
  getPaginatedLocations,
  checkAccountStatusWithLocations,
  deleteNestedLocations,
  createNestedLocation,
  updateNestedLocation,
  exportNestedLocations,
  importNestedLocations,
  exportNestedLocationsTemplate,
  exportLegacyLocations,
};
